import {Component, ConfigOptions} from 'panel';
import {registerMPElement} from 'common/util/register-element';
import {serializeAttributes} from 'common/util';

import template from './index.jade';
import css from './index.styl?inline';
import {submitZendeskTicket, uploadZendeskFile} from './api';
import {BillingAccount, RequestSubtype, RequestType, SupportPlanTier, ZendeskTicketData, ZendeskUpload} from './types';
import {getSupportPlan, getSupportPlanTier} from './utils';

// 5 is the max file upload limit in the Zendesk Web Widget
const MAX_FILE_UPLOADS = 5;

const LOCAL_STORAGE_KEY = `support_form_draft`;

const enum RequestTypeLabel {
  HelpUsingMixpanel = `I need help using Mixpanel`,
  HelpWithMyAccount = `I need help with my account`,
  ReportAnIssue = `I need to report an issue`,
}

/**
 * The tags here should exactly match the tags defined in
 * the following fields at https://mixpanelsupport.zendesk.com
 *
 * Request Type (Field ID: 360021982112)
 * Request Subtype (Field ID: 360022979492)
 */
const REQUEST_TYPES = {
  [RequestTypeLabel.HelpUsingMixpanel]: {
    fieldName: `Product area`,
    tag: RequestType.HelpUsingMixpanel,
    options: {
      Dashboard: RequestSubtype.DashboardHelp,
      Analysis: RequestSubtype.AnalysisHelp,
      Users: RequestSubtype.UsersHelp,
      'Messages & Experiments': RequestSubtype.MessagesAndExperimentsHelp,
      'Data Management': RequestSubtype.DataManagementHelp,
      Integrations: RequestSubtype.IntegrationsHelp,
      Pipelines: RequestSubtype.PipelinesHelp,
      Implementation: RequestSubtype.ImplementationHelp,
      Other: RequestSubtype.OtherHelp,
    },
  },

  [RequestTypeLabel.HelpWithMyAccount]: {
    fieldName: `Account area`,
    tag: RequestType.HelpWithMyAccount,
    options: {
      'Billing, Payments, and Pricing': RequestSubtype.BillingPaymentsAndPricing,
      'Account Management': RequestSubtype.AccountManagement,
      'Personal Data and GDPR': RequestSubtype.PersonalDataAndGdpr,
      Other: RequestSubtype.OtherHelp,
    },
  },

  [RequestTypeLabel.ReportAnIssue]: {
    fieldName: `Issue`,
    tag: RequestType.ReportAnIssue,
    options: {
      Bug: RequestSubtype.Bug,
      'Reports not Loading': RequestSubtype.ReportsNotLoading,
      'Messages not Sending': RequestSubtype.MessagesNotSending,
      'Data Ingestion Issues': RequestSubtype.DataIngestionIssues,
      Other: RequestSubtype.OtherHelp,
    },
  },
};

interface ElementRefs {
  fileInput?: HTMLInputElement;
  textarea?: HTMLTextAreaElement;
}

export interface SupportWidgetState {
  billingAccount: BillingAccount;
  comment: string;
  isDraggingAndDropping: boolean;
  isMaximized: boolean;
  isMinimized: boolean;
  isTicketSubmitting: boolean;
  isTicketSubmitted: boolean;
  isUploadingFiles: boolean;
  requestSubtypeLabel: string;
  requestTypeLabel: string;
  subject: string;
  textareaHeight: number;
  uploads: ZendeskUpload[];
}

class SupportWidget extends Component<SupportWidgetState> {
  private elRefs: ElementRefs = {};

  static get attrsSchema() {
    return {
      'billing-account-data': `json`,
      'data-state': `json`,
      'events-plan-id': `number`,
      'feature-gate-name': `string`,
      'people-plan-id': `number`,
      'project-id': `number`,
      'total-arr': `number`,
      'user-age-in-days': `number`,
      'user-email': `string`,
      'user-id': `number`,
      'user-name': `string`,
    };
  }

  get config(): ConfigOptions<SupportWidgetState> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        billingAccount: null,
        comment: null,
        isDraggingAndDropping: false,
        isMaximized: false,
        isMinimized: false,
        isTicketSubmitting: false,
        isTicketSubmitted: false,
        isUploadingFiles: false,
        requestSubtypeLabel: null,
        requestTypeLabel: null,
        subject: null,
        textareaHeight: null,
        uploads: [],
      },
      helpers: {
        MAX_FILE_UPLOADS,

        serializeAttributes,

        closeSupportWidget: () => {
          this.dispatchEvent(new CustomEvent(`close`));
          window.localStorage.removeItem(LOCAL_STORAGE_KEY);
        },

        fileInputDragleave: () => {
          this.update({isDraggingAndDropping: false});
        },

        fileInputDragover: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          ev.dataTransfer.dropEffect = `copy`;
          this.update({isDraggingAndDropping: true});
        },

        getRequestSubtypeOptions: () => {
          return Object.keys(REQUEST_TYPES[this.state.requestTypeLabel].options);
        },

        getRequestSubtypeTicketField: () => {
          return REQUEST_TYPES[this.state.requestTypeLabel].fieldName;
        },

        getRequestTypeOptions: () => {
          return Object.keys(REQUEST_TYPES);
        },

        handleInputComment: (ev) => {
          // + 2 for 2x textarea border width
          this.update({
            comment: ev.target.value,
            textareaHeight: this.elRefs.textarea.scrollHeight + 2,
          });
        },

        handlePasteFromClipboard: (ev) => {
          if (ev.clipboardData.files.length > 0) {
            ev.preventDefault();
            this.helpers.uploadFilesToZendesk(ev.clipboardData.files);
          }
        },

        inputSubject: (ev) => {
          this.update({subject: ev.target.value});
        },

        isSubmitDisabled: () => {
          const {comment, isTicketSubmitting, requestSubtypeLabel, requestTypeLabel, subject} = this.state;
          return !(subject && comment && requestTypeLabel && requestSubtypeLabel) || isTicketSubmitting;
        },

        maximizeWidget: () => {
          this.update({
            isMaximized: true,
            isMinimized: false,
          });
          this.dispatchEvent(new CustomEvent(`maximize`));
        },

        removeUploadedFile: (index) => {
          const uploads = [...this.state.uploads];
          uploads.splice(index, 1);
          this.update({uploads});
        },

        saveDraft: () => {
          const {subject, comment, uploads, requestTypeLabel, requestSubtypeLabel} = this.state;
          if (subject || comment || uploads.length > 0) {
            window.localStorage.setItem(
              LOCAL_STORAGE_KEY,
              JSON.stringify({
                subject,
                comment,
                uploads,
                requestTypeLabel,
                requestSubtypeLabel,
              }),
            );
          }
        },

        selectRequestSubtype: (ev) => {
          this.update({requestSubtypeLabel: ev.detail.value});
        },

        selectRequestType: (ev) => {
          this.update({
            requestSubtypeLabel: null,
            requestTypeLabel: ev.detail.value,
          });
        },

        setElRef: (ref: string, el: HTMLElement) => {
          this.elRefs[ref] = el;
        },

        showFreePlanWarning: () => {
          const {billingAccount, requestTypeLabel} = this.state;
          return (
            requestTypeLabel === RequestTypeLabel.HelpUsingMixpanel &&
            billingAccount &&
            billingAccount.supportPlanTier === SupportPlanTier.Free
          );
        },

        submitZendeskTicket: async () => {
          this.update({isTicketSubmitting: true});
          const {billingAccount, comment, requestSubtypeLabel, requestTypeLabel, subject, uploads} = this.state;
          const zendeskTicketData: ZendeskTicketData = {
            eventsPlanId: this.attr(`events-plan-id`),
            featureGateName: this.attr(`feature-gate-name`),
            peoplePlanId: this.attr(`people-plan-id`),
            requestSubtype: REQUEST_TYPES[requestTypeLabel].options[requestSubtypeLabel],
            requestType: REQUEST_TYPES[requestTypeLabel].tag,
            userAgeInDays: this.attr(`user-age-in-days`),
            userEmail: this.attr(`user-email`),
            userId: this.attr(`user-id`),
            userName: this.attr(`user-name`),
            billingAccount,
            comment,
            subject,
            uploads,
          };
          await submitZendeskTicket(zendeskTicketData);
          this.update({
            comment: null,
            isTicketSubmitted: true,
            isTicketSubmitting: false,
            subject: null,
          });
          window.localStorage.removeItem(LOCAL_STORAGE_KEY);
        },

        toggleFullscreen: () => {
          if (this.state.isMaximized) {
            this.dispatchEvent(new CustomEvent(`dock`));
          } else {
            this.helpers.maximizeWidget();
          }
        },

        toggleMinimize: () => {
          if (this.state.isMinimized) {
            this.dispatchEvent(new CustomEvent(`dock`));
          }
          this.update({isMinimized: !this.state.isMinimized});
        },

        uploadDroppedFiles: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          this.update({isDraggingAndDropping: false});
          this.helpers.uploadFilesToZendesk(ev.dataTransfer.files);
        },

        uploadFilesToZendesk: async (files: FileList) => {
          this.update({isUploadingFiles: true});
          const numUploads = Math.min(files.length, MAX_FILE_UPLOADS - this.state.uploads.length);
          for (let i = 0; i < numUploads; i++) {
            const upload = await uploadZendeskFile(files[i]);
            const uploads: ZendeskUpload[] = [...this.state.uploads, upload];
            this.update({uploads});
          }
          this.update({isUploadingFiles: false});
        },

        uploadSelectedFiles: () => {
          this.helpers.uploadFilesToZendesk(this.elRefs.fileInput.files);
        },
      },

      hooks: {
        postUpdate: () => {
          this.dispatchEvent(new CustomEvent(`state-change`, {detail: this.state}));
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();
    const supportFormDraft = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (supportFormDraft) {
      this.update(JSON.parse(supportFormDraft));
    }
    window.addEventListener(`beforeunload`, this.helpers.saveDraft);
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    // This ensures the full-page /get-support modal that loads right away will
    // work, since `billing-account-data` is fetched from an async request and
    // state.billingAccount would stay null if set in connectedCallback()
    if (attr === `billing-account-data`) {
      const {billing_account, service_plan_ids} = this.attr(`billing-account-data`);
      this.update({
        billingAccount: {
          id: billing_account.id,
          serviceArr: billing_account.recurring_service_arr,
          supportPlan: getSupportPlan(service_plan_ids),
          supportPlanTier: getSupportPlanTier(billing_account.arr),
          totalArr: billing_account.arr,
        },
      });
    }
  }

  public disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(`beforeunload`, this.helpers.saveDraft);
  }
}

registerMPElement(`support-widget`, SupportWidget);
